import { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  Film,
  Hash,
  Image as ImageIcon,
  Loader2,
  Play,
  Save,
  Sparkles,
  X,
} from 'lucide-react';
import { ImageUploadField } from '../../components/ui/ImageUploadField';
import {
  useCreateGalleryItem,
  useUpdateGalleryItem,
} from '../../services/httpServices/queries';
import type { GalleryItemType, WebsiteGalleryItem } from '../../services/httpServices/websiteService';

interface WebsiteGalleryItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The asset being edited, or null/undefined when adding a new one. */
  item?: WebsiteGalleryItem | null;
  onSaved?: () => void;
}

interface GalleryForm {
  type: GalleryItemType;
  title: string;
  image: string;
  videoUrl: string;
  isActive: boolean;
  displayOrder: number;
}

const EMPTY_FORM: GalleryForm = {
  type: 'image',
  title: '',
  image: '',
  videoUrl: '',
  isActive: true,
  displayOrder: 0,
};

/** Matches youtube.com/watch?v=, youtu.be/, /embed/ and /shorts/ links. */
const YOUTUBE_URL_REGEX =
  /^https?:\/\/(www\.|m\.)?(youtube\.com\/(watch\?v=|embed\/|shorts\/)|youtu\.be\/)[\w-]{6,}/i;

/** Pulls the 11-ish character video id out of any common YouTube URL shape. */
function youtubeId(url: string): string | null {
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/i,
  );
  return match ? match[1] : null;
}

function validate(form: GalleryForm): Record<string, string> {
  const errors: Record<string, string> = {};

  if (form.type === 'image') {
    if (!form.image.trim()) errors.image = 'Upload an image to continue.';
    return errors;
  }

  if (!form.videoUrl.trim()) {
    errors.videoUrl = 'A YouTube URL is required.';
  } else if (!YOUTUBE_URL_REGEX.test(form.videoUrl.trim())) {
    errors.videoUrl = 'Enter a valid YouTube video URL.';
  }
  if (!form.image.trim()) errors.image = 'Upload a thumbnail for this video.';
  return errors;
}

/**
 * Add/edit dialog for a single gallery asset. Images upload their own file;
 * videos take a YouTube URL plus a custom thumbnail uploaded through our media
 * endpoint, so the public gallery grid stays visually consistent.
 */
export function WebsiteGalleryItemModal({
  isOpen,
  onClose,
  item,
  onSaved,
}: WebsiteGalleryItemModalProps) {
  const isEdit = !!item?.id;
  const createMutation = useCreateGalleryItem();
  const updateMutation = useUpdateGalleryItem();

  const [form, setForm] = useState<GalleryForm>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setForm(
      item
        ? {
            type: item.type,
            title: item.title ?? '',
            image: item.image ?? '',
            videoUrl: item.videoUrl ?? '',
            isActive: item.isActive,
            displayOrder: item.displayOrder ?? 0,
          }
        : EMPTY_FORM,
    );
    setErrors({});
    setGeneralError('');
  }, [isOpen, item]);

  const setField = <K extends keyof GalleryForm>(field: K, value: GalleryForm[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const previewSrc = useMemo(() => {
    if (form.image) return form.image;
    if (form.type === 'video') {
      const id = youtubeId(form.videoUrl);
      if (id) return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
    }
    return '';
  }, [form.image, form.type, form.videoUrl]);

  const saving = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const fieldErrors = validate(form);
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }

    setGeneralError('');
    const payload = {
      type: form.type,
      title: form.title.trim() || undefined,
      image: form.image.trim(),
      videoUrl: form.type === 'video' ? form.videoUrl.trim() : '',
      isActive: form.isActive,
      displayOrder: Number(form.displayOrder) || 0,
    };

    try {
      if (isEdit && item) {
        await updateMutation.mutateAsync({ id: item.id, data: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      onSaved?.();
      onClose();
    } catch (err: any) {
      setGeneralError(
        err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.',
      );
    }
  };

  if (!isOpen) return null;

  const inputBase =
    'w-full px-3 py-2 border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 transition-colors';
  const ic = (field: string) =>
    `${inputBase} ${errors[field] ? 'border-danger focus:ring-danger' : 'border-border focus:ring-primary'}`;
  const labelClass = 'block text-sm font-medium text-text mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-surface rounded-2xl border border-border shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border bg-background/50 rounded-t-2xl sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              {form.type === 'video' ? (
                <Film size={20} className="text-primary" />
              ) : (
                <ImageIcon size={20} className="text-primary" />
              )}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-text">
                {isEdit ? 'Edit gallery asset' : 'Add gallery asset'}
              </h2>
              <p className="text-xs text-text-muted">
                Images use an uploaded file; videos use a YouTube URL with a custom thumbnail.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-background rounded-xl transition-colors"
            aria-label="Close"
          >
            <X size={18} className="text-text-muted" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          {generalError && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-danger/10 border border-danger/20 text-danger text-sm">
              <AlertCircle size={16} />
              <span>{generalError}</span>
            </div>
          )}

          {/* Type selector */}
          <div>
            <label className={labelClass}>Asset type</label>
            <div className="grid grid-cols-2 gap-2 p-1 bg-background border border-border rounded-xl">
              {(
                [
                  { value: 'image', label: 'Image', icon: ImageIcon, hint: 'JPG, PNG, WEBP' },
                  { value: 'video', label: 'Video', icon: Film, hint: 'YouTube + thumbnail' },
                ] as const
              ).map((option) => {
                const Icon = option.icon;
                const active = form.type === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setField('type', option.value)}
                    className={`flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                      active
                        ? 'bg-primary text-white shadow-sm'
                        : 'text-text-muted hover:text-text hover:bg-surface'
                    }`}
                  >
                    <Icon size={16} />
                    <span>{option.label}</span>
                    <span className={`text-[11px] ${active ? 'text-white/70' : 'text-text-muted/70'}`}>
                      · {option.hint}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Left: fields */}
            <div className="space-y-4">
              {form.type === 'video' && (
                <div>
                  <label className={labelClass}>YouTube URL *</label>
                  <input
                    type="url"
                    autoFocus
                    className={ic('videoUrl')}
                    placeholder="https://www.youtube.com/watch?v=…"
                    value={form.videoUrl}
                    onChange={(e) => setField('videoUrl', e.target.value)}
                  />
                  {errors.videoUrl ? (
                    <p className="text-xs text-danger mt-1">{errors.videoUrl}</p>
                  ) : (
                    <p className="text-xs text-text-muted mt-1">
                      Paste a watch, share or Shorts link — we only store the URL.
                    </p>
                  )}
                </div>
              )}

              <div>
                <label className={labelClass}>
                  {form.type === 'video' ? 'Thumbnail *' : 'Image *'}
                </label>
                <ImageUploadField
                  value={form.image}
                  onChange={(url) => setField('image', url)}
                  error={errors.image}
                  previewClassName="h-44"
                  helperText={
                    form.type === 'video'
                      ? 'Upload a thumbnail from our system — it is shown in the gallery grid.'
                      : 'Upload the image to show in the gallery.'
                  }
                />
              </div>

              <div>
                <label className={labelClass}>Title / caption</label>
                <input
                  type="text"
                  className={ic('title')}
                  placeholder="Optional caption shown under the asset"
                  value={form.title}
                  maxLength={255}
                  onChange={(e) => setField('title', e.target.value)}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>
                    <span className="inline-flex items-center gap-1.5">
                      <Hash size={13} /> Display order
                    </span>
                  </label>
                  <input
                    type="number"
                    min={0}
                    className={inputBase + ' border-border focus:ring-primary'}
                    value={form.displayOrder}
                    onChange={(e) => setField('displayOrder', Number(e.target.value))}
                  />
                </div>
                <div>
                  <label className={labelClass}>Status</label>
                  <button
                    type="button"
                    onClick={() => setField('isActive', !form.isActive)}
                    className={`flex items-center justify-between w-full px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      form.isActive
                        ? 'border-success/40 bg-success/10 text-success'
                        : 'border-border bg-background text-text-muted'
                    }`}
                  >
                    <span>{form.isActive ? 'Active' : 'Inactive'}</span>
                    <span
                      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                        form.isActive ? 'bg-success' : 'bg-border'
                      }`}
                    >
                      <span
                        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                          form.isActive ? 'translate-x-[18px]' : 'translate-x-0.5'
                        }`}
                      />
                    </span>
                  </button>
                </div>
              </div>
            </div>

            {/* Right: live preview */}
            <div className="space-y-2">
              <label className={labelClass}>
                <span className="inline-flex items-center gap-1.5">
                  <Sparkles size={13} className="text-primary" /> Live preview
                </span>
              </label>
              <div className="rounded-xl border border-border bg-background overflow-hidden">
                <div className="relative aspect-video bg-background">
                  {previewSrc ? (
                    <img src={previewSrc} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-text-muted">
                      {form.type === 'video' ? <Film size={28} /> : <ImageIcon size={28} />}
                      <span className="text-xs">Nothing to preview yet</span>
                    </div>
                  )}

                  <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-medium text-white backdrop-blur-sm">
                    {form.type === 'video' ? <Film size={11} /> : <ImageIcon size={11} />}
                    {form.type === 'video' ? 'Video' : 'Image'}
                  </span>

                  {form.type === 'video' && (
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/85 shadow-lg">
                        <Play size={20} className="text-primary ml-0.5" fill="currentColor" />
                      </span>
                    </span>
                  )}
                </div>
                <div className="px-3 py-2.5 border-t border-border">
                  <p className="text-sm font-medium text-text truncate">
                    {form.title.trim() || (form.type === 'video' ? 'Untitled video' : 'Untitled image')}
                  </p>
                  <p className="text-xs text-text-muted">
                    {form.isActive ? 'Visible on the website' : 'Hidden from the website'}
                  </p>
                </div>
              </div>
              <p className="text-xs text-text-muted">
                This is how the asset appears in the public gallery grid.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              {isEdit ? 'Save changes' : 'Add asset'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
